import React, {useCallback, useState} from 'react';

import {assert} from 'Asserts';
import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ToastMessage from 'nav/toasts/ToastMessage';
import notifications from 'notifications';
import isProcedureVersionPoW from 'procedures/design/utils/isProcedureVersionPoW';
import {ProcedureRunPlanConditionREST, updateProcedureRunPlan} from 'procedures/network/procedureRunPlanDAO';
import {deriveConfirmationMethodByParamVersionId} from 'procedures/planning/buildFullExperimentPlan';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as modals from 'util/Modals';
import * as net from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import {ExperimentFlowchart, prepareInitialExperimentalInputs} from '../design-state/ExperimentalInputs';
import {
  PlanConditionsByMethodId,
  resizeExperimentalInputsToPlanConditions,
} from '../design-state/planConditionState';
import EditProcedureModal, {
  EditProcedureModalMode,
  ProcedureChangeParams,
} from '../procedure-selection/EditProcedureModal';
import {preserveProcedureInputsOnVersionUpdate} from '../procedure-selection/preserveProcedureInputsOnVersionUpdate';
import {ProcedureVersionUpdateCheckResult} from '../procedure-selection/useProcedureVersionUpdateCheck';

interface UsePlanProcedureActionsParams {
  buildPreservedConditionIdMappings: (
    planConditionsByMethodId: PlanConditionsByMethodId,
    oldFlowchart: ExperimentFlowchart,
    newFlowchart: ExperimentFlowchart
  ) => Array<Record<string, string>>;
  planConditionsByMethodId: PlanConditionsByMethodId;
  planConditionsForFlowchartChange: (
    planConditionsByMethodId: PlanConditionsByMethodId,
    oldFlowchart: ExperimentFlowchart,
    newFlowchart: ExperimentFlowchart
  ) => PlanConditionsByMethodId;
  experimentalInputs: ExperimentalInputs;
  flowchartVersionIdForNavigation: string;
  linkedFlowchartVersionId: string;
  procedureId: string;
  procedureName: string;
  procedureRunPlanId: string;
  procedureString: string;
  procedureType: ProcedureType;
  refetch: () => void;
  serializePreservedInputsForNetwork: (
    preservedInputs: ExperimentalInputs,
    planConditionsByMethodId: PlanConditionsByMethodId,
    methodIdsInOrder: ReadonlyArray<string>
  ) => Array<ProcedureRunPlanConditionREST>;
  studyId: string;
  studyType: StudyType;
  templateFlowchart: ExperimentFlowchart;
  versionUpdateCheck: ProcedureVersionUpdateCheckResult;
}

interface UsePlanProcedureActionsResult {
  handleApplyProcedureUpdate: () => Promise<void>;
  handleChangeProcedure: (params: ProcedureChangeParams) => Promise<void>;
  handleOpenApplyUpdateModal: () => Promise<void>;
  handleRemoveProcedure: () => Promise<void>;
}

/**
 * Provides plan actions that replace, update, remove, or switch the selected procedure design.
 */
export default function usePlanProcedureActions({
  buildPreservedConditionIdMappings,
  planConditionsByMethodId,
  planConditionsForFlowchartChange,
  experimentalInputs,
  flowchartVersionIdForNavigation,
  linkedFlowchartVersionId,
  procedureId,
  procedureName,
  procedureRunPlanId,
  procedureString,
  procedureType,
  refetch,
  serializePreservedInputsForNetwork,
  studyId,
  studyType,
  templateFlowchart,
  versionUpdateCheck,
}: UsePlanProcedureActionsParams): UsePlanProcedureActionsResult {
  const [isApplyingUpdate, setIsApplyingUpdate] = useState(false);

  /**
   * Handles changing the procedure on a procedure run plan.
   * Updates the plan with the new flowchart version while preserving condition metadata
   * (names, descriptions, replicates) and resetting planned values to the selected procedure's defaults.
   */
  const handleChangeProcedure = useCallback(
    async (params: ProcedureChangeParams): Promise<void> => {
      const newFlowchart = params.procedure.procedureFlowchart;
      const newMethodIds = params.procedure.methods.map((method) => method.id);
      const conditionsForNewProcedure = planConditionsForFlowchartChange(
        planConditionsByMethodId,
        templateFlowchart,
        newFlowchart
      );
      const defaultInputsForNewProcedure = resizeExperimentalInputsToPlanConditions(
        conditionsForNewProcedure,
        // The count is 0 because resizing builds each method's columns from its condition rows.
        prepareInitialExperimentalInputs(0, params.procedure.methods)
      );
      const confirmationMethodByParamVersionId = deriveConfirmationMethodByParamVersionId(
        defaultInputsForNewProcedure
      );
      const conditionsForUpdate = serializePreservedInputsForNetwork(
        defaultInputsForNewProcedure,
        conditionsForNewProcedure,
        newMethodIds
      ).map((condition) => ({
        ...condition,
        // Unit operation links are specific to the old procedure and must be cleared.
        replicateInfos: condition.replicateInfos.map((replicateInfo) => ({
          ...replicateInfo,
          sourcePMEIIds: [],
        })),
      }));

      await updateProcedureRunPlan({
        id: procedureRunPlanId,
        studyId,
        // XOR validation handled by EditProcedureModal - only one of these will be set
        templateProcedureFlowchartVersionId: params.templateProcedureFlowchartVersionId,
        workflowFlowchartConfigVersionId: params.workflowFlowchartConfigVersionId,
        experimentConditions: conditionsForUpdate,
        confirmationMethodByParamVersionId,
      });

      refetch();
    },
    [
      planConditionsByMethodId,
      procedureRunPlanId,
      refetch,
      serializePreservedInputsForNetwork,
      studyId,
      planConditionsForFlowchartChange,
      templateFlowchart,
    ]
  );

  /**
   * Compares the current and new procedure versions and preserves inputs and confirmation methods for
   * parameters/materials/equipment whose underlying procedure elements have
   * matching stable parameter IDs (as determined by preserveProcedureInputsOnVersionUpdate).
   */
  const handleApplyProcedureUpdate = useCallback(async (): Promise<void> => {
    if (
      isApplyingUpdate ||
      !versionUpdateCheck.hasNewerVersion ||
      !versionUpdateCheck.latestPublishedVersion
    ) {
      return;
    }

    setIsApplyingUpdate(true);

    try {
      const newFlowchart = versionUpdateCheck.latestPublishedVersion;
      // Row counts come from the remapped conditions, which resize these matched columns below.
      const matchedInputs = preserveProcedureInputsOnVersionUpdate(
        experimentalInputs,
        templateFlowchart,
        newFlowchart,
        0
      );
      const preservedConditions = planConditionsForFlowchartChange(
        planConditionsByMethodId,
        templateFlowchart,
        newFlowchart
      );
      const preservedInputs = resizeExperimentalInputsToPlanConditions(preservedConditions, matchedInputs);
      const confirmationMethodByParamVersionId = deriveConfirmationMethodByParamVersionId(preservedInputs);
      const preservedConditionIdMappings = buildPreservedConditionIdMappings(
        planConditionsByMethodId,
        templateFlowchart,
        newFlowchart
      );
      const newMethodIds = newFlowchart.nodes.map((method) => method.id);
      const conditionsForUpdate = serializePreservedInputsForNetwork(
        preservedInputs,
        preservedConditions,
        newMethodIds
      ).map((condition) => {
        assert(
          condition.templateMethodVersionId != null,
          'Expected templateMethodVersionId for version update condition row'
        );
        const templateMethodVersionId = condition.templateMethodVersionId;
        const preservedConditionId =
          preservedConditionIdMappings[condition.position]?.[templateMethodVersionId];
        return {
          ...condition,
          id: preservedConditionId ?? undefined,
          // Keep existing unit operation links for rows that keep the same condition id.
          // For newly created rows, links reference old PMDVs and must be cleared.
          replicateInfos: condition.replicateInfos.map((replicateInfo) =>
            preservedConditionId != null
              ? replicateInfo
              : {
                  ...replicateInfo,
                  sourcePMEIIds: [],
                }
          ),
        };
      });

      await withLoadingScreen(
        updateProcedureRunPlan({
          id: procedureRunPlanId,
          studyId,
          templateProcedureFlowchartVersionId: versionUpdateCheck.isPoW ? undefined : newFlowchart.id,
          workflowFlowchartConfigVersionId: versionUpdateCheck.isPoW ? newFlowchart.id : undefined,
          experimentConditions: conditionsForUpdate,
          confirmationMethodByParamVersionId,
        }),
        `Updating to latest ${procedureString} version...`
      );

      notifications.success(`Updated to latest ${procedureString} version.`);
      refetch();
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      if (net.isServerRequestError(error)) {
        notifications.error(
          <ToastMessage
            header={`Failed to apply ${procedureString} update`}
            body={notifications.getFriendlyErrorAsElement(error as Response, '')}
          />
        );
        return;
      }
      notifications.error(`Failed to apply ${procedureString} update. Please try again.`);
    } finally {
      setIsApplyingUpdate(false);
    }
  }, [
    buildPreservedConditionIdMappings,
    planConditionsByMethodId,
    planConditionsForFlowchartChange,
    experimentalInputs,
    isApplyingUpdate,
    procedureRunPlanId,
    procedureString,
    refetch,
    serializePreservedInputsForNetwork,
    studyId,
    templateFlowchart,
    versionUpdateCheck,
  ]);

  const handleOpenApplyUpdateModal = useCallback(async (): Promise<void> => {
    if (!versionUpdateCheck.hasNewerVersion || !versionUpdateCheck.latestPublishedVersion) {
      return;
    }

    await legacyRunModal(EditProcedureModal, {
      mode: EditProcedureModalMode.VERSION_UPDATE,
      procedureId,
      flowchartVersionId: flowchartVersionIdForNavigation,
      outdatedFlowchartVersionId: flowchartVersionIdForNavigation,
      currentFlowchartVersionId: versionUpdateCheck.latestPublishedVersion.id,
      studyType,
      procedureType,
      onApplyVersionUpdate: handleApplyProcedureUpdate,
    });
  }, [
    flowchartVersionIdForNavigation,
    handleApplyProcedureUpdate,
    procedureId,
    procedureType,
    studyType,
    versionUpdateCheck.hasNewerVersion,
    versionUpdateCheck.latestPublishedVersion,
  ]);

  const handleRemoveProcedure = useCallback(async (): Promise<void> => {
    const shouldRemove = (await Promise.resolve(
      modals.confirm(
        `Remove ${procedureString}?`,
        <>
          Removing <strong>{procedureName}</strong> will clear all conditions and settings from this run plan.
        </>,
        ButtonStyle.NEGATIVE,
        {
          acceptText: 'Remove',
          rejectText: 'Cancel',
          enableRedesign: true,
          promiseCompatible: true,
        }
      )
    )) as boolean;

    if (!shouldRemove) {
      return;
    }

    const clearLinkedProcedureParams = isProcedureVersionPoW(linkedFlowchartVersionId)
      ? {workflowFlowchartConfigVersionId: null}
      : {templateProcedureFlowchartVersionId: null};

    await withLoadingScreen(
      updateProcedureRunPlan({
        id: procedureRunPlanId,
        studyId,
        ...clearLinkedProcedureParams,
        experimentConditions: [],
        confirmationMethodByParamVersionId: {},
      }),
      `Removing ${procedureString} from plan...`
    );

    refetch();
  }, [linkedFlowchartVersionId, procedureName, procedureRunPlanId, procedureString, refetch, studyId]);

  return {
    handleApplyProcedureUpdate,
    handleChangeProcedure,
    handleOpenApplyUpdateModal,
    handleRemoveProcedure,
  };
}
